import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { devicesApi, Device, DeviceCondition } from '../../api/devices.api';
import { productsApi } from '../../api/products.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { categoriesApi, Category } from '../../api/categories.api';
import { brandsApi, Brand } from '../../api/brands.api';
import { modelsApi, Model } from '../../api/models.api';
import { companiesApi } from '../../api/companies.api';
import { personnelApi, Personnel } from '../../api/personnel.api';
import { serviceProvidersApi, ServiceProvider } from '../../api/service-providers.api';
import { departmentsApi } from '../../api/departments.api';
import { inventoryApi } from '../../api/inventory.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import BarcodeModal from '../../components/common/BarcodeModal';
import Toast from '../../components/common/Toast';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';
import SearchableSelect from '../../components/forms/SearchableSelect';
import { useToast } from '../../hooks/useToast';

const DeviceList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [selectedBranch, setSelectedBranch] = useState<string>('all');
  const [selectedCompany, setSelectedCompany] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [departments, setDepartments] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 50;
  const { toast, showToast, hideToast } = useToast();
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ success: number; failed: number; errors: any[] } | null>(null);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [formData, setFormData] = useState({
    product: '',
    hasSerialNumber: true,
    serialNumber: '',
    quantity: 1,
    assetTag: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: 0,
    warrantyEndDate: '',
    condition: DeviceCondition.New,
    specifications: '',
    notes: '',
    // Movement fields - same as inventory page
    movementType: 'BranchTransfer',
    personnel: '',
    toLocationId: '',
    toLocation: '',
  });

  // Read URL parameters and set filters
  useEffect(() => {
    const status = searchParams.get('status');
    const branch = searchParams.get('branch');
    const company = searchParams.get('company');
    const category = searchParams.get('category');
    const search = searchParams.get('search');

    if (status) {
      setSelectedStatus(status);
    }
    if (branch) {
      // Find branch by name
      const foundBranch = branches.find(b => b.name === decodeURIComponent(branch));
      if (foundBranch) {
        setSelectedBranch(foundBranch.id);
      }
    }
    if (company) {
      // Find company by name
      const foundCompany = companies.find(c => c.name === decodeURIComponent(company));
      if (foundCompany) {
        setSelectedCompany(foundCompany.id);
      }
    }
    if (category) {
      // Find category by name
      const foundCategory = categories.find(c => c.name === decodeURIComponent(category));
      if (foundCategory) {
        setSelectedCategory(foundCategory.id);
      }
    }
    if (search) {
      setSearchQuery(search);
    }
  }, [searchParams, branches, companies, categories]);

  useEffect(() => {
    fetchProducts();
    fetchBranches();
    fetchCategories();
    fetchBrands();
    fetchModels();
    fetchCompanies();
    fetchPersonnel();
    fetchServiceProviders();
    fetchDepartments();
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [currentPage, searchQuery, selectedProduct, selectedBranch, selectedCompany, selectedStatus, selectedCategory, selectedDepartment]);

  const fetchProducts = async () => {
    try {
      const allProducts = await productsApi.getAll();
      setProducts(allProducts.sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Ürünler yüklenirken hata:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const allBranches = await branchesApi.getAll();
      setBranches(allBranches.sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Şubeler yüklenirken hata:', err);
    }
  };

  const fetchCategories = async () => {
    try {
      const allCategories = await categoriesApi.getAll();
      setCategories(allCategories.filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Kategoriler yüklenirken hata:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const allBrands = await brandsApi.getAll();
      setBrands(allBrands.filter((b) => b.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Markalar yüklenirken hata:', err);
    }
  };

  const fetchModels = async () => {
    try {
      const allModels = await modelsApi.getAll();
      setModels(allModels.filter((m) => m.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
      console.log('Models loaded from API:', allModels.filter((m) => m.isActive).map(m => m.name));
    } catch (err) {
      console.error('Modeller yüklenirken hata:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const allCompanies = await companiesApi.getAll();
      setCompanies(allCompanies.filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Şirketler yüklenirken hata:', err);
    }
  };

  const fetchPersonnel = async () => {
    try {
      const allPersonnel = await personnelApi.getAll();
      setPersonnel(allPersonnel.filter((p) => p.isActive).sort((a, b) => 
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`, 'tr')
      ));
    } catch (err) {
      console.error('Personel yüklenirken hata:', err);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const data = await serviceProvidersApi.getAll();
      setServiceProviders(data.filter((s) => s.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Servisler yüklenirken hata:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentsApi.getAll();
      setDepartments(data.filter((d) => d.isActive).sort((a, b) => a.name.localeCompare(b.name, 'tr')));
    } catch (err) {
      console.error('Departmanlar yüklenirken hata:', err);
    }
  };

  const fetchDevices = async () => {
    try {
      setIsLoading(true);
      const response = await devicesApi.getAllPaginated(
        currentPage, 
        itemsPerPage, 
        searchQuery || undefined,
        selectedProduct !== 'all' ? selectedProduct : undefined,
        selectedBranch !== 'all' ? selectedBranch : undefined,
        selectedCompany !== 'all' ? selectedCompany : undefined,
        selectedStatus !== 'all' ? selectedStatus : undefined,
        selectedCategory !== 'all' ? selectedCategory : undefined,
        selectedDepartment !== 'all' ? selectedDepartment : undefined
      );
      setDevices(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.total);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Cihazlar yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const searchProducts = async (query: string) => {
    try {
      const results = await productsApi.search(query);
      return results.map((p) => {
        // Handle brand - it can be an object or string
        let brandName = '';
        if (p.brand) {
          if (typeof p.brand === 'object' && p.brand !== null) {
            brandName = (p.brand as any).name || '';
          } else {
            brandName = p.brand;
          }
        }
        
        // Format: "Product Name - Brand - Model"
        let label = p.name;
        if (brandName) {
          label += ` - ${brandName}`;
        }
        if (p.model) {
          label += ` - ${p.model}`;
        }
        
        return {
          value: p.id,
          label,
        };
      });
    } catch (err) {
      console.error('Ürün arama hatası:', err);
      return [];
    }
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on search
    fetchDevices();
  };

  const handleExportPDF = async () => {
    try {
      setIsLoading(true);
      const blob = await devicesApi.exportPDF(
        searchQuery || undefined,
        selectedProduct !== 'all' ? selectedProduct : undefined,
        selectedBranch !== 'all' ? selectedBranch : undefined,
        selectedCompany !== 'all' ? selectedCompany : undefined
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cihaz-listesi-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('PDF başarıyla indirildi', 'success');
    } catch (err: any) {
      setError(err.message || 'PDF oluşturulurken hata oluştu');
      showToast('PDF oluşturulurken hata oluştu', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsLoading(true);
      const blob = await devicesApi.downloadImportTemplate();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cihaz-import-sablonu-${new Date().getTime()}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showToast('Şablon başarıyla indirildi', 'success');
    } catch (err: any) {
      setError(err.message || 'Şablon indirilirken hata oluştu');
      showToast('Şablon indirilirken hata oluştu', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    console.log('File selected:', file.name);

    try {
      setIsImporting(true);
      
      // Refresh products, brands, categories, models before parsing Excel
      console.log('Refreshing products, brands, categories, and models...');
      await Promise.all([
        fetchProducts(),
        fetchBrands(),
        fetchCategories(),
        fetchModels(),
      ]);
      console.log('Data refreshed successfully');
      
      // Read and parse Excel file
      const reader = new FileReader();
      
      reader.onerror = (error) => {
        console.error('FileReader error:', error);
        showToast('Dosya okunamadı', 'error');
        setIsImporting(false);
      };
      
      reader.onload = async (e) => {
        try {
          console.log('File loaded, parsing...');
          const data = e.target?.result;
          if (!data) {
            console.error('No data from FileReader');
            showToast('Dosya boş', 'error');
            setIsImporting(false);
            return;
          }

          // Use xlsx library to parse
          console.log('Importing xlsx library...');
          const XLSX = await import('xlsx');
          console.log('xlsx imported, reading workbook...');
          
          const workbook = XLSX.read(data, { type: 'binary' });
          console.log('Workbook read, sheets:', workbook.SheetNames);
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 0 });
          console.log('Parsed rows:', jsonData.length);

          // Map to preview format
          const preview = jsonData.map((row: any, index: number) => {
            // Normalize values by trimming whitespace
            const branch = (row['Şube'] || row['branch'] || '').toString().trim();
            const brand = (row['Marka'] || row['brand'] || '').toString().trim();
            const category = (row['Kategori'] || row['category'] || '').toString().trim();
            const model = (row['Model'] || row['model'] || '').toString().trim();
            const serialNumber = (row['Seri numarası'] || row['serialNumber'] || '').toString().trim();
            
            console.log(`Row ${index + 2}: model="${model}"`);
            
            return {
              rowNumber: index + 2, // Excel row (1-indexed + header)
              branch,
              brand,
              category,
              model,
              serialNumber,
              isValid: true,
              errors: [] as string[],
            };
          });

          // Sort by product name (brand + model) to group same products together
          preview.sort((a, b) => {
            const productA = `${a.brand} ${a.model}`.toLowerCase();
            const productB = `${b.brand} ${b.model}`.toLowerCase();
            return productA.localeCompare(productB, 'tr');
          });

          console.log('Preview data prepared and sorted:', preview.length, 'rows');
          console.log('Available models:', models.map(m => m.name));
          console.log('Available categories:', categories.map(c => c.name));
          console.log('Available brands:', brands.map(b => b.name));
          console.log('Sample preview row:', preview[0]);
          setPreviewData(preview);
          setIsPreviewModalOpen(true);
          setIsImporting(false);
        } catch (err) {
          console.error('Excel parse error:', err);
          showToast('Excel dosyası okunamadı: ' + (err as Error).message, 'error');
          setIsImporting(false);
        }
      };
      
      console.log('Starting to read file...');
      reader.readAsBinaryString(file);
    } catch (err: any) {
      console.error('Import error:', err);
      setError(err.message || 'Dosya okunamadı');
      showToast('Dosya okunamadı: ' + err.message, 'error');
      setIsImporting(false);
    } finally {
      // Reset file input
      event.target.value = '';
    }
  };

  const handleConfirmImport = async () => {
    if (previewData.length === 0) return;

    try {
      setIsImporting(true);
      
      // Convert preview data to import format
      const importData = previewData.map(row => ({
        branch: row.branch,
        brand: row.brand,
        category: row.category,
        model: row.model,
        serialNumber: row.serialNumber,
      }));
      
      const result = await devicesApi.importFromData(importData);
      setImportResult(result);
      
      if (result.failed === 0) {
        showToast(`${result.success} cihaz başarıyla içe aktarıldı`, 'success');
      } else {
        showToast(`${result.success} başarılı, ${result.failed} başarısız`, 'warning');
      }
      
      await fetchDevices();
      setIsPreviewModalOpen(false);
      setPreviewData([]);
    } catch (err: any) {
      setError(err.message || 'İçe aktarma başarısız oldu');
      showToast('İçe aktarma başarısız oldu', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCancelImport = () => {
    setIsPreviewModalOpen(false);
    setPreviewData([]);
    setIsImporting(false);
  };

  const handleUpdatePreviewRow = (index: number, field: string, value: string) => {
    const updated = [...previewData];
    updated[index] = { ...updated[index], [field]: value };
    setPreviewData(updated);
  };

  const handleDeletePreviewRow = (index: number) => {
    const updated = previewData.filter((_, i) => i !== index);
    setPreviewData(updated);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedProduct('all');
    setSelectedBranch('all');
    setSelectedCompany('all');
    setSelectedDepartment('all');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleOpenModal = (device?: Device) => {
    if (device) {
      setEditingDevice(device);
      const productId = typeof device.product === 'object' ? (device.product as any)._id || (device.product as any).id : device.product;
      setFormData({
        product: productId,
        hasSerialNumber: !!device.serialNumber && device.serialNumber !== device.id,
        serialNumber: device.serialNumber || '',
        quantity: 1,
        assetTag: device.assetTag || '',
        purchaseDate: device.purchaseDate?.split('T')[0] || '',
        purchasePrice: device.purchasePrice,
        warrantyEndDate: device.warrantyEndDate?.split('T')[0] || '',
        condition: device.condition,
        specifications: device.specifications || '',
        notes: device.notes || '',
        movementType: 'BranchTransfer',
        personnel: '',
        toLocationId: '',
        toLocation: '',
      });
    } else {
      setEditingDevice(null);
      setFormData({
        product: '',
        hasSerialNumber: true,
        serialNumber: '',
        quantity: 1,
        assetTag: '',
        purchaseDate: new Date().toISOString().split('T')[0],
        purchasePrice: 0,
        warrantyEndDate: '',
        condition: DeviceCondition.New,
        specifications: '',
        notes: '',
        movementType: 'BranchTransfer',
        personnel: '',
        toLocationId: '',
        toLocation: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingDevice(null);
  };

  const handleMovementTypeChange = (type: string) => {
    setFormData({
      ...formData,
      movementType: type,
      personnel: '',
      toLocationId: '',
      toLocation: type === 'Scrap' ? 'Hurda' : '',
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        // For update, send device-specific fields including product
        const updateData = {
          product: formData.product,
          serialNumber: formData.serialNumber,
          assetTag: formData.assetTag,
          purchaseDate: formData.purchaseDate,
          purchasePrice: formData.purchasePrice,
          warrantyEndDate: formData.warrantyEndDate || undefined,
          condition: formData.condition,
          specifications: formData.specifications,
          notes: formData.notes,
        };
        await devicesApi.update(editingDevice.id, updateData);
        showToast('Cihaz başarıyla güncellendi', 'success');
      } else {
        // For create, only send device creation fields (no movement fields)
        const createData = {
          product: formData.product,
          hasSerialNumber: formData.hasSerialNumber,
          serialNumber: formData.serialNumber,
          quantity: formData.quantity,
          assetTag: formData.assetTag,
          purchaseDate: formData.purchaseDate,
          purchasePrice: formData.purchasePrice,
          warrantyEndDate: formData.warrantyEndDate || undefined,
          condition: formData.condition,
          specifications: formData.specifications,
          notes: formData.notes,
        };
        
        const createdDevices = await devicesApi.create(createData);
        const createdDevice = Array.isArray(createdDevices) ? createdDevices[0] : createdDevices;
        
        console.log('Created device:', createdDevice);
        console.log('Target location ID:', formData.toLocationId);
        console.log('Movement type:', formData.movementType);
        
        // If target location is specified and different from current branch, change location
        if (createdDevice && formData.toLocationId && formData.movementType === 'BranchTransfer') {
          try {
            console.log('Changing location to:', formData.toLocationId);
            await devicesApi.changeLocation(createdDevice.id, formData.toLocationId);
            console.log('Location changed successfully');
          } catch (locationErr) {
            console.error('Konum değiştirilemedi:', locationErr);
            showToast('Cihaz eklendi ancak konum değiştirilemedi', 'warning');
          }
        }
        
        // Create movement record for new device
        if (createdDevice && formData.serialNumber) {
          try {
            // Clean up empty strings to undefined for optional fields
            const movementData: any = {
              product: createdDevice.id, // Use device ID, not product ID
              type: formData.movementType,
              serialNumber: formData.serialNumber,
              date: formData.purchaseDate,
              toLocation: formData.toLocation,
              toLocationId: formData.toLocationId || undefined,
              personnel: formData.personnel || undefined,
              notes: formData.notes || undefined,
            };

            console.log('Creating movement record:', movementData);
            await inventoryApi.createMovement(movementData);
            console.log('Movement record created successfully');
          } catch (movementErr) {
            console.error('Hareket kaydı oluşturulamadı:', movementErr);
            // Device created but movement failed - show warning
            showToast('Cihaz eklendi ancak hareket kaydı oluşturulamadı', 'warning');
          }
        }
        
        showToast('Cihaz başarıyla eklendi', 'success');
      }
      await fetchDevices();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
      showToast('İşlem başarısız oldu', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu cihazı silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await devicesApi.delete(id);
      await fetchDevices();
      showToast('Cihaz başarıyla silindi', 'success');
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
      showToast('Silme işlemi başarısız oldu', 'error');
    }
  };

  const handleOpenLocationModal = (device: Device) => {
    setSelectedDevice(device);
    setLocationSearchQuery('');
    setIsLocationModalOpen(true);
  };

  const handleCloseLocationModal = () => {
    setIsLocationModalOpen(false);
    setSelectedDevice(null);
    setLocationSearchQuery('');
  };

  const handleLocationChange = async (newBranchId: string) => {
    if (!newBranchId || !selectedDevice) return;
    
    try {
      await devicesApi.changeLocation(selectedDevice.id, newBranchId);
      await fetchDevices();
      showToast('Konum başarıyla değiştirildi', 'success');
      handleCloseLocationModal();
    } catch (err: any) {
      setError(err.message || 'Konum değiştirme başarısız oldu');
      showToast('Konum değiştirme başarısız oldu', 'error');
    }
  };

  const handleShowBarcode = (device: Device) => {
    setSelectedDevice(device);
    setIsBarcodeModalOpen(true);
  };

  const handleCloseBarcodeModal = () => {
    setIsBarcodeModalOpen(false);
    setSelectedDevice(null);
  };

  const getProductName = (product: any, device?: Device): string => {
    if (typeof product === 'object' && product !== null) {
      return product.name || '-';
    }
    // If product is null but device has name field, use it
    if (device?.name) {
      return device.name;
    }
    return '-';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, string> = {
      Available: 'bg-green-100 text-green-800',
      Assigned: 'bg-blue-100 text-blue-800',
      InRepair: 'bg-yellow-100 text-yellow-800',
      Retired: 'bg-gray-100 text-gray-800',
      Lost: 'bg-red-100 text-red-800',
      Scrapped: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      Available: 'Şubede',
      Assigned: 'Zimmetli',
      InRepair: 'Onarımda',
      Retired: 'Kullanım Dışı',
      Lost: 'Kayıp',
      Scrapped: 'Hurda',
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const columns: Column<Device>[] = [
    {
      key: 'serialNumber',
      header: 'Seri No',
      render: (item) => {
        if (!item.serialNumber) return '-';
        // Check if serial number is MongoDB ID (24 hex chars)
        const isMongoId = /^[a-f\d]{24}$/i.test(item.serialNumber);
        return isMongoId ? (
          <span className="text-xs text-gray-500" title="Otomatik oluşturuldu">
            {item.serialNumber.substring(0, 8)}...
          </span>
        ) : (
          item.serialNumber
        );
      },
    },
    { key: 'product', header: 'Ürün', render: (item) => getProductName(item.product, item) },
    { 
      key: 'category', 
      header: 'Kategori', 
      render: (item) => {
        if (!item.category) return '-';
        
        // If category is already populated as object
        if (typeof item.category === 'object' && item.category !== null) {
          return (item.category as any).name || '-';
        }
        
        // If category is string (ObjectId), try to find it in categories list
        if (typeof item.category === 'string') {
          const category = categories.find(c => c.id === item.category);
          return category?.name || '-';
        }
        
        return '-';
      }
    },
    { key: 'assetTag', header: 'Varlık Etiketi', render: (item) => item.assetTag || '-' },
    { key: 'status', header: 'Durum', render: (item) => getStatusBadge(item.status) },
    {
      key: 'currentLocation',
      header: 'Konum',
      render: (item) => {
        // If device is in repair, show repair location without change button
        if (item.status === 'InRepair' && item.currentLocation?.name) {
          return (
            <div className="flex items-center gap-2">
              <span className="text-sm text-yellow-700">
                🔧 {item.currentLocation.name}
              </span>
            </div>
          );
        }
        
        // Try to get branch name for other statuses
        let branchName = '-';
        if (item.branch) {
          branchName = typeof item.branch === 'object' 
            ? (item.branch as any).name || '-'
            : branches.find(b => b.id === item.branch)?.name || '-';
        } else if (item.currentLocation?.id) {
          const branchId = typeof item.currentLocation.id === 'object'
            ? (item.currentLocation.id as any)._id || (item.currentLocation.id as any).id
            : item.currentLocation.id;
          branchName = branches.find(b => b.id === branchId)?.name || '-';
        }
        
        return (
          <div className="flex items-center gap-2">
            <span className="text-sm">{branchName}</span>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={(e) => {
                e.stopPropagation();
                handleOpenLocationModal(item);
              }}
            >
              Değiştir
            </Button>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="primary" onClick={() => handleShowBarcode(item)}>
            🏷️ Barkod
          </Button>
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];



  if (isLoading) {
    return <Loading text="Envanter yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Envanter</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleDownloadTemplate}>
            📥 Örnek Excel İndir
          </Button>
          <Button 
            variant="outline" 
            onClick={() => document.getElementById('excel-upload-input')?.click()}
            disabled={isImporting}
          >
            {isImporting ? '⏳ Yükleniyor...' : '📤 Excel Yükle'}
          </Button>
          <input
            id="excel-upload-input"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleImportExcel}
            className="hidden"
            disabled={isImporting}
          />
          <Button variant="outline" onClick={handleExportPDF}>
            📄 PDF İndir
          </Button>
          <Button onClick={() => handleOpenModal()}>Yeni Cihaz Ekle</Button>
        </div>
      </div>

      {importResult && (
        <div className={`mb-4 p-4 rounded-md ${importResult.failed === 0 ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
          <h3 className="font-semibold mb-2">İçe Aktarma Sonucu</h3>
          <p className="text-sm">
            ✅ Başarılı: {importResult.success} | ❌ Başarısız: {importResult.failed}
          </p>
          {importResult.errors.length > 0 && (
            <details className="mt-2">
              <summary className="cursor-pointer text-sm font-medium">Hataları Göster</summary>
              <div className="mt-2 max-h-40 overflow-y-auto">
                {importResult.errors.map((err, idx) => (
                  <div key={idx} className="text-xs p-2 bg-white rounded mb-1">
                    <span className="font-medium">Satır {err.row}:</span> {err.error}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <div className="mb-4 flex justify-between items-center">
        <Button 
          variant="outline" 
          onClick={() => setShowFilters(!showFilters)}
          size="sm"
        >
          {showFilters ? '🔼 Filtreleri Gizle' : '🔽 Filtreleri Göster'}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Filtreler</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="mb-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Seri No / Ürün Adı
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Seri no veya ürün adı ile ara..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Select
              label="Ürün"
              value={selectedProduct}
              onChange={(value) => {
                setSelectedProduct(value);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'Tüm Ürünler' },
                ...products.map((product) => ({
                  value: product.id,
                  label: product.name
                }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Şube"
              value={selectedBranch}
              onChange={(value) => {
                setSelectedBranch(value);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'Tüm Şubeler' },
                ...branches.map((branch) => ({
                  value: branch.id,
                  label: branch.name
                }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Şirket"
              value={selectedCompany}
              onChange={(value) => {
                setSelectedCompany(value);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'Tüm Şirketler' },
                ...companies.map((company) => ({
                  value: company.id,
                  label: company.name
                }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
            <Select
              label="Departman"
              value={selectedDepartment}
              onChange={(value) => {
                setSelectedDepartment(value);
                setCurrentPage(1);
              }}
              options={[
                { value: 'all', label: 'Tüm Departmanlar' },
                ...departments.map((department) => ({
                  value: department.id,
                  label: department.name
                }))
              ]}
              searchable={true}
              containerClassName="mb-0"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleSearch} variant="secondary">
              Ara
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Filtreleri Temizle
            </Button>
          </div>
        </div>
      )}

      <Table
        data={devices}
        columns={columns}
        onRowClick={(device) => navigate(`/inventory?serialNumber=${device.serialNumber}`)}
        emptyMessage="Henüz cihaz bulunmamaktadır"
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage,
          onPageChange: handlePageChange,
        }}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingDevice ? 'Cihaz Düzenle' : 'Yeni Cihaz Ekle'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <SearchableSelect
            label="Ürün"
            value={formData.product}
            onChange={(value) => setFormData({ ...formData, product: value })}
            onSearch={searchProducts}
            placeholder="Ürün aramak için en az 3 karakter girin..."
            required
            minSearchLength={3}
            helperText="Ürün adı, marka veya model ile arama yapabilirsiniz"
          />

          {!editingDevice && (
            <>
              <div className="mb-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hasSerialNumber}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        hasSerialNumber: e.target.checked,
                        serialNumber: e.target.checked ? formData.serialNumber : '',
                        quantity: e.target.checked ? 1 : formData.quantity,
                      })
                    }
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Seri numarası var</span>
                </label>
              </div>

              {formData.hasSerialNumber ? (
                <Input
                  label="Seri Numarası"
                  value={formData.serialNumber}
                  onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
                  required
                  placeholder="Cihazın seri numarasını girin"
                />
              ) : (
                <Input
                  label="Adet"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: Number(e.target.value) })}
                  required
                  helperText="Seri numarası olmayan cihazlar için adet belirtin (MongoDB ID kullanılacak)"
                />
              )}
            </>
          )}

          {editingDevice && (
            <Input
              label="Seri Numarası"
              value={formData.serialNumber}
              onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
              required
              placeholder="Cihazın seri numarasını girin"
            />
          )}

          <Input
            label="Varlık Etiketi"
            value={formData.assetTag}
            onChange={(e) => setFormData({ ...formData, assetTag: e.target.value })}
            placeholder="Örn: NCITY-KDS2"
            required
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Satın Alma Tarihi"
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => setFormData({ ...formData, purchaseDate: e.target.value })}
              required
            />
            <Input
              label="Satın Alma Fiyatı"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => setFormData({ ...formData, purchasePrice: Number(e.target.value) })}
              required
            />
          </div>
          <Input
            label="Garanti Bitiş Tarihi"
            type="date"
            value={formData.warrantyEndDate}
            onChange={(e) => setFormData({ ...formData, warrantyEndDate: e.target.value })}
          />
          <Select
            label="Durum"
            value={formData.condition}
            onChange={(value) => setFormData({ ...formData, condition: value as DeviceCondition })}
            options={[
              { value: DeviceCondition.New, label: 'Yeni' },
              { value: DeviceCondition.Good, label: 'İyi' },
              { value: DeviceCondition.Fair, label: 'Orta' },
              { value: DeviceCondition.Poor, label: 'Kötü' },
              { value: DeviceCondition.Broken, label: 'Bozuk' },
            ]}
            searchable={true}
            required
          />
          <Input
            label="Teknik Özellikler"
            value={formData.specifications}
            onChange={(e) => setFormData({ ...formData, specifications: e.target.value })}
            placeholder="Opsiyonel"
          />
          
          {!editingDevice && (
            <>
              <div className="mt-6 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-semibold text-blue-900 mb-1">📦 Hareket Bilgileri</p>
                <p className="text-xs text-blue-700">Cihaz eklenirken otomatik hareket kaydı oluşturulacak</p>
              </div>
              
              <Select
                label="Hareket Tipi"
                value={formData.movementType}
                onChange={(value) => handleMovementTypeChange(value)}
                options={[
                  { value: 'BranchTransfer', label: 'Şube Transferi' },
                  { value: 'Assignment', label: 'Personele Zimmet' },
                  { value: 'Return', label: 'İade' },
                  { value: 'Repair', label: 'Onarıma Gönder' },
                  { value: 'Transfer', label: 'Diğer Transfer' },
                  { value: 'Scrap', label: 'Hurda' },
                ]}
                searchable={true}
                required
              />

              {formData.movementType === 'BranchTransfer' && (
                <Select
                  label="Hedef Şube"
                  value={formData.toLocationId}
                  onChange={(value) => {
                    const branch = branches.find((b) => b.id === value);
                    setFormData({
                      ...formData,
                      toLocationId: value,
                      toLocation: branch?.name || '',
                    });
                  }}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                  placeholder="Şube seçiniz"
                  searchable={true}
                  required
                />
              )}

              {formData.movementType === 'Assignment' && (
                <Select
                  label="Personel"
                  value={formData.personnel}
                  onChange={(value) => {
                    const person = personnel.find((p) => p.id === value);
                    setFormData({
                      ...formData,
                      personnel: value,
                      toLocationId: value,
                      toLocation: person ? `${person.firstName} ${person.lastName}` : '',
                    });
                  }}
                  options={personnel.map((p) => ({ value: p.id, label: `${p.firstName} ${p.lastName}` }))}
                  placeholder="Personel seçiniz"
                  searchable={true}
                  required
                />
              )}

              {formData.movementType === 'Repair' && (
                <Select
                  label="Servis"
                  value={formData.toLocationId}
                  onChange={(value) => {
                    const service = serviceProviders.find((s) => s.id === value);
                    setFormData({
                      ...formData,
                      toLocationId: value,
                      toLocation: service?.name || '',
                    });
                  }}
                  options={serviceProviders.map((s) => ({ value: s.id, label: s.name }))}
                  placeholder="Servis seçiniz"
                  searchable={true}
                  required
                />
              )}

              {(formData.movementType === 'Return' || formData.movementType === 'Transfer') && (
                <Input
                  label="Hedef Konum"
                  value={formData.toLocation}
                  onChange={(e) => setFormData({ ...formData, toLocation: e.target.value })}
                  placeholder="Örn: Depo, Merkez, vb."
                  required
                />
              )}

              {formData.movementType === 'Scrap' && (
                <Input
                  label="Hedef Konum"
                  value="Hurda"
                  disabled
                  helperText="Cihaz hurda olarak işaretlenecek ve stoktan düşecek"
                />
              )}
            </>
          )}
          
          <Input
            label="Notlar"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Opsiyonel"
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingDevice ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      <BarcodeModal
        isOpen={isBarcodeModalOpen}
        onClose={handleCloseBarcodeModal}
        serialNumber={selectedDevice?.serialNumber || ''}
        productName={selectedDevice ? getProductName(selectedDevice.product, selectedDevice) : ''}
      />

      <Modal
        isOpen={isLocationModalOpen}
        onClose={handleCloseLocationModal}
        title="Konum Değiştir"
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Cihaz
            </label>
            <div className="p-3 bg-gray-50 rounded-md">
              <p className="text-sm font-medium">
                {selectedDevice ? getProductName(selectedDevice.product, selectedDevice) : ''}
              </p>
              <p className="text-xs text-gray-500">
                Seri No: {selectedDevice?.serialNumber || '-'}
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Arama
            </label>
            <input
              type="text"
              value={locationSearchQuery}
              onChange={(e) => setLocationSearchQuery(e.target.value)}
              placeholder="Şube adı ile ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Yeni Konum Seçin
            </label>
            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md">
              {branches
                .filter(branch => 
                  branch.name.toLowerCase().includes(locationSearchQuery.toLowerCase()) ||
                  branch.code?.toLowerCase().includes(locationSearchQuery.toLowerCase())
                )
                .map((branch) => {
                  // Get current branch ID
                  let currentBranchId = '';
                  if (selectedDevice?.branch) {
                    currentBranchId = typeof selectedDevice.branch === 'object' 
                      ? (selectedDevice.branch as any)._id || (selectedDevice.branch as any).id 
                      : selectedDevice.branch;
                  } else if (selectedDevice?.currentLocation?.id) {
                    currentBranchId = typeof selectedDevice.currentLocation.id === 'object'
                      ? (selectedDevice.currentLocation.id as any)._id || (selectedDevice.currentLocation.id as any).id
                      : selectedDevice.currentLocation.id;
                  }
                  
                  const isCurrentLocation = currentBranchId === branch.id;
                  
                  return (
                    <button
                      key={branch.id}
                      onClick={() => handleLocationChange(branch.id)}
                      disabled={isCurrentLocation}
                      className={`w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-blue-50 transition-colors ${
                        isCurrentLocation ? 'bg-blue-100 cursor-not-allowed' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {branch.name}
                          </p>
                          {branch.code && (
                            <p className="text-xs text-gray-500">
                              Kod: {branch.code}
                            </p>
                          )}
                        </div>
                        {isCurrentLocation && (
                          <span className="text-xs text-blue-600 font-medium">
                            Mevcut Konum
                          </span>
                        )}
                      </div>
                    </button>
                  );
                })}
              {branches.filter(branch => 
                branch.name.toLowerCase().includes(locationSearchQuery.toLowerCase()) ||
                branch.code?.toLowerCase().includes(locationSearchQuery.toLowerCase())
              ).length === 0 && (
                <div className="px-4 py-8 text-center text-gray-500">
                  <p className="text-sm">Şube bulunamadı</p>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" fullWidth onClick={handleCloseLocationModal}>
              İptal
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPreviewModalOpen}
        onClose={handleCancelImport}
        title="Excel Önizleme ve Düzenleme"
        size="full"
      >
        <div className="flex flex-col h-[80vh]">
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-md mb-4">
            <p className="text-sm text-blue-900">
              📋 Toplam {previewData.length} satır bulundu. Verileri kontrol edin ve gerekirse düzenleyin.
            </p>
            <p className="text-xs text-blue-700 mt-1">
              💡 Ürün adı otomatik oluşturulur: <strong>Marka + Model</strong>
            </p>
          </div>

          <div className="flex-1 overflow-auto border border-gray-300 rounded-md mb-4">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">Satır</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">Şube</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[150px]">Marka</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[120px]">Kategori</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[150px]">Model</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]">Ürün Adı (Önizleme)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase min-w-[150px]">Seri No</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">İşlem</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {previewData.map((row, index) => {
                  const productName = row.brand && row.model ? `${row.brand} ${row.model}` : '-';
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm text-gray-500">{row.rowNumber}</td>
                      <td className="px-3 py-2">
                        <select
                          value={row.branch}
                          onChange={(e) => handleUpdatePreviewRow(index, 'branch', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seçiniz...</option>
                          {branches.map((branch) => (
                            <option key={branch.id} value={branch.name}>
                              {branch.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={row.brand}
                          onChange={(e) => handleUpdatePreviewRow(index, 'brand', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seçiniz...</option>
                          {brands.map((brand) => (
                            <option key={brand.id} value={brand.name}>
                              {brand.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={row.category}
                          onChange={(e) => handleUpdatePreviewRow(index, 'category', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seçiniz...</option>
                          {categories.map((category) => (
                            <option key={category.id} value={category.name}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select
                          value={row.model}
                          onChange={(e) => {
                            console.log(`Model changed for row ${index}: "${e.target.value}"`);
                            handleUpdatePreviewRow(index, 'model', e.target.value);
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="">Seçiniz...</option>
                          {models.map((model) => (
                            <option key={model.id} value={model.name}>
                              {model.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-sm text-gray-700 font-medium bg-gray-50 px-2 py-1 rounded">
                          {productName}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={row.serialNumber}
                          onChange={(e) => handleUpdatePreviewRow(index, 'serialNumber', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                          placeholder="Seri no yazın..."
                        />
                      </td>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => handleDeletePreviewRow(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-bold"
                          title="Satırı sil"
                        >
                          ✕
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex gap-2 pt-4 border-t border-gray-200 bg-white">
            <Button 
              type="button" 
              onClick={handleConfirmImport} 
              fullWidth
              disabled={isImporting || previewData.length === 0}
            >
              {isImporting ? '⏳ Yükleniyor...' : `✅ ${previewData.length} Cihazı Yükle`}
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              fullWidth 
              onClick={handleCancelImport}
              disabled={isImporting}
            >
              İptal
            </Button>
          </div>
        </div>
      </Modal>

      {toast.isVisible && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default DeviceList;
